import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import moment from 'moment';
import { MatPaginator } from '@angular/material/paginator';
import { PrintService } from 'src/app/services/print.service';

@Component({
  selector: 'app-students-inventory',
  templateUrl: './students-inventory.component.html',
  styleUrls: ['./students-inventory.component.css']
})
export class StudentsInventoryComponent extends ViewTableComponent implements OnInit {
  isActiveChecked = false;
  isInactiveChecked = false;
  lastLessonTakenStart: Date;
  lastLessonTakenEnd: Date;
  override filters: string = '?field_archive=1&';

  private _printService: PrintService;

  override ngOnInit() {
    super.ngOnInit();
    this._printService = new PrintService(this._drupalRESTService, this._authService);
  }

  // A student account can reference multiple contacts (e.g. a couple). The
  // endpoint returns one row per account with the primary in the headline and
  // every non-retired contact in contacts[]. Stack the secondary names under
  // the primary in the Full Name cell.
  secondaryContacts(element: any): any[] {
    const contacts = element?.contacts ?? [];
    return contacts.filter(
      (c: any) => String(c.contact_id) !== String(element?.primary_contact_id) && (c.name ?? '').trim() !== ''
    );
  }

  override displayedColumns: string[] = [
    'name',
    'field_student_department',
    'address',
    'totalEnrolled',
    'lessonUsed',
    'remaining',
    'totalDueLessons',
    'miscService',
    'total_due_misc_field',
    'lastLesson',
    'futureLesson'
  ];

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;
    this.queryParams = params;

    // Handle different checkbox based filters.
    let start, end;
    let filters = '?field_archive=1&';
    if (params) {
      for (const param of params) {
        if (param?.parameter === 'Active') {
          filters += "&field_last_lesson_taken_value=" + moment(this.reportDateService.currentStudioDateKey(), 'YYYY-MM-DD').subtract(30, 'days').format('YYYY-MM-DD') + "--";
        }
        if (param?.parameter === 'Inactive') {
          filters += "&field_last_lesson_taken_value=" + "--" + moment(this.reportDateService.currentStudioDateKey(), 'YYYY-MM-DD').subtract(30, 'days').format('YYYY-MM-DD');
        }
        if (param?.parameter === 'NFA') {
          filters += "&field_nfa=1";
          filters += "&field_total_lessons_remaining_value[value]=0";
          filters += "&field_total_lessons_remaining_value[min]=";
          filters += "&field_total_lessons_remaining_value[max]=";
          filters += "&field_total_lessons_remaining_value_op=>";
        }
        if (param?.parameter === 'paid_finished') {
          filters += "&field_paid_finished=true";
          filters += "&field_total_lessons_paid_ahead_value=0";
          filters += "&field_total_lessons_remaining_value[value]=0";
          filters += "&field_total_lessons_remaining_value[min]=";
          filters += "&field_total_lessons_remaining_value[max]=";
          filters += "&field_total_lessons_remaining_value_op==";
        }

        if (param?.parameter === 'last_lesson_taken_start') {
          start = param.value;
        }
        if (param?.parameter === 'last_lesson_taken_end') {
          end = param.value;
        }
      }

      if (start && end) {
        filters += "&field_last_lesson_taken_value=" + start + "--" + end;
      }
    }

    this.filters = filters;

    // Load the new data with the filtering parameters.
    let endpoint: string = "/api/v2/students_inventory" + filters;

    this._drupalRESTService.httpGET(endpoint, params)
      .subscribe((data) => {
        this.ShowProgressBar = false;

        this.dataSource = new MatTableDataSource(data['results']);

        if (data['results']) {
          this.setupPagination(data, params);
        }
      },
        error => this.handleError(error)
      );
  }

  onActiveChange() {
    if (this.isActiveChecked) {
      this.isInactiveChecked = false;
    }
    this.submitFilters();
  }

  onInactiveChange() {
    if (this.isInactiveChecked) {
      this.isActiveChecked = false;
    }
    this.submitFilters();
  }

  submitFilters() {
    const params = [];
    if (this.isActiveChecked) {
      params.push({ parameter: 'Active', value: 'true' });
    }
    if (this.isInactiveChecked) {
      params.push({ parameter: 'Inactive', value: 'true' });
    }
    if (this.lastLessonTakenStart) {
      params.push({ parameter: 'last_lesson_taken_start', value: this.reportDateService.formatCalendarParam(this.lastLessonTakenStart) });
    }
    if (this.lastLessonTakenEnd) {
      params.push({ parameter: 'last_lesson_taken_end', value: this.reportDateService.formatCalendarParam(this.lastLessonTakenEnd) });
    }
    this.getData(params);
  }

  downloadStudentsInventoryReport() {
    const filtersSegment = this.filters?.startsWith('?') ? this.filters : `?${this.filters ?? 'field_archive=1&'}`;
    const route = `/api/v2/students_inventory/export${filtersSegment}`;
    const exportParams = (this.queryParams ?? [])
      .filter(param => param.parameter !== 'page' && param.parameter !== 'items_per_page')
      .map(param => ({ ...param }));

    const formatParam = exportParams.find(param => param.parameter === '_format');
    if (formatParam) {
      formatParam.value = 'csv';
    } else {
      exportParams.push({ parameter: '_format', value: 'csv' });
    }

    this.downloadCSVDrupalViews(route, 'student_inventory', exportParams);
  }

  printStudentInventory() {
    const rows: any[] = this.dataSource?.data ?? [];
    const reportTitle = 'Student Inventory';

    const escape = (v: any) => {
      const s = v === null || v === undefined ? '' : String(v);
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    };

    // Mirror the on-screen pipes: dates via reportDateService, money as a
    // 2-decimal amount with thousands separators (US-centric report).
    const formatDate = (v: any) => this.reportDateService.formatDate(v);
    const formatMoney = (v: any) => {
      const n = parseFloat(v);
      if (isNaN(n)) return '';
      return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    // Strip the misc-services HTML (<div> per service) into newline-separated text.
    const miscToText = (html: any) => {
      const s = html === null || html === undefined ? '' : String(html);
      return s.replace(/<\/div>/gi, '\n').replace(/<[^>]+>/g, '').trim();
    };

    const headers = [
      'Last Name', 'First Name', 'Dept.', 'Address',
      'Enrolled', 'Used', 'Untaught',
      'Lesson $ Due', 'Misc Services', 'Misc $ Due',
      'Last Lesson', 'Future Lesson'
    ];
    const headerHtml = headers.map(h => `<th>${escape(h)}</th>`).join('');

    const bodyHtml = rows.map((r: any) => {
      // Stack every named contact so a couple reads naturally in one row.
      const contacts = (r.contacts ?? []).filter((c: any) => (c.name ?? '').trim() !== '');
      const lastNames = contacts.map((c: any) => c.last_name).filter((x: string) => (x ?? '').trim());
      const firstNames = contacts.map((c: any) => c.first_name).filter((x: string) => (x ?? '').trim());
      const lastCell = lastNames.length ? lastNames.map(escape).join('<br>') : escape(r.field_last_name);
      const firstCell = firstNames.length ? firstNames.map(escape).join('<br>') : escape(r.field_first_name);
      const address = [
        r.field_address_address_line1, r.field_address_address_line2,
        r.field_address_administrative_area, r.field_address_postal_code
      ].filter((x: any) => (x ?? '').toString().trim()).join(' ');
      const miscText = miscToText(r.misc_services_field);
      return `<tr>
        <td>${lastCell}</td>
        <td>${firstCell}</td>
        <td>${escape(r.field_student_department)}</td>
        <td>${escape(address)}</td>
        <td class="num">${escape(r.field_total_lessons_enrolled ?? '')}</td>
        <td class="num">${escape(r.field_total_lessons_taken_all ?? '')}</td>
        <td class="num">${escape(r.field_total_lessons_remaining ?? '')}</td>
        <td class="num">${escape(formatMoney(r.total_due_lessons_field))}</td>
        <td>${escape(miscText).replace(/\n/g, '<br>')}</td>
        <td class="num">${escape(formatMoney(r.total_due_misc_field))}</td>
        <td class="nowrap">${escape(formatDate(r.field_last_lesson_taken))}</td>
        <td class="nowrap">${escape(formatDate(r.field_upcoming_lesson))}</td>
      </tr>`;
    }).join('');

    const countHtml = `<div class="row-count">${rows.length} student account${rows.length === 1 ? '' : 's'}</div>`;

    const content = `
      <h1 class="report-title">${escape(reportTitle)}</h1>
      ${countHtml}
      <table class="report-table">
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${bodyHtml}</tbody>
      </table>
    `;

    const styles = `
      @page { size: letter landscape; margin: 0.5in; }
      .report-table { width: 100%; border-collapse: collapse; font-size: 9px; }
      .report-table th, .report-table td { border: 1px solid #ccc; padding: 3px 5px; vertical-align: top; }
      .report-table th { background: #f0f0f0; }
      .report-table td.num { text-align: right; white-space: nowrap; }
      .report-table td.nowrap { white-space: nowrap; }
      .row-count { margin: 4px 0 8px 0; font-size: 11px; color: #555; }
    `;

    this._printService.print(content, {
      title: reportTitle,
      styles,
      showSchoolName: true,
      promptPrint: true
    });
  }

  updatePickerMax(event: any) {
    this.lastLessonTakenStart = event.value;
    this.submitFilters();
  }

  override resetForm() {
    this.isActiveChecked = false;
    this.isInactiveChecked = false;
    this.lastLessonTakenStart = null;
    this.lastLessonTakenEnd = null;
    this.submitFilters();
  }

  override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(param => param.parameter === 'page');
    if (this.paginator) {
      this.paginator.length = data?.['count'] ?? 0;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    }
    // DO NOT call updateRouterParams here - prevents duplicate requests
  }
}
